import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useEscapeKey } from '../hooks/useEscapeKey';
import '../styles/LimitReachedModal.css';

/**
 * LimitReachedModal Component
 *
 * Shows a beautiful modal when free users hit their list/place limits.
 *
 * Props:
 * - isOpen: boolean - Whether the modal is visible
 * - onClose: function - Handler to close the modal
 * - type: 'lists' | 'places' - What limit was hit
 * - limit: number - The limit they hit (e.g., 10 for lists, 30 for places)
 * - current: number (optional) - Current count
 */
function LimitReachedModal({ isOpen, onClose, type, limit, current }) {
  const navigate = useNavigate();

  useEscapeKey(isOpen, onClose);

  if (!isOpen) return null;

  const handleUpgrade = () => {
    onClose();
    navigate('/settings/premium');
  };

  const isListLimit = type === 'lists';
  const title = isListLimit ? 'List Limit Reached' : 'Place Limit Reached';
  const description = isListLimit
    ? `You've created ${limit} lists, which is the maximum for free accounts.`
    : `This list has ${limit} places, which is the maximum for free accounts.`;
  const benefit = isListLimit
    ? 'Create unlimited lists to organize all your favorite spots'
    : 'Add unlimited places to each list';

  return (
    <div className="limit-modal-overlay" onClick={onClose}>
      <div className="limit-modal" onClick={(e) => e.stopPropagation()}>
        <button className="limit-modal-close" onClick={onClose} aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="limit-modal-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
              fill="currentColor"
            />
          </svg>
        </div>

        <h2 className="limit-modal-title">{title}</h2>
        <p className="limit-modal-description">{description}</p>

        <div className="limit-modal-benefit">
          <div className="benefit-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <span>{benefit}</span>
        </div>

        <div className="limit-modal-pricing">
          <span className="price">$5</span>
          <span className="period">/month</span>
        </div>

        <div className="limit-modal-actions">
          <button className="limit-modal-upgrade" onClick={handleUpgrade}>
            Upgrade to Premium
          </button>
          <button className="limit-modal-dismiss" onClick={onClose}>
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
}

export default LimitReachedModal;
